import { useAsclepius } from "@/composables/api/asclepius"
import { reactive, ref, watch } from "vue"
import {
  AppointmentRequest,
  AppointmentRequestApiListAppointmentRequestsRequest,
  Availability,
  CancelAppointmentRequestRequest,
  FulfillAppointmentRequestRequest,
  ListAppointmentRequestResponse,
  ListRecommendedAvailabilitiesResponse
} from "@vitable/asclepius-api-client-ts"

export const useListAppointmentRequests = () => {
  const { getAppointmentRequestApi, callWithResponse, callResult } = useAsclepius()

  const appointmentRequests = reactive<Paginated<AppointmentRequest>>({
    currentPage: 1,
    nextPage: undefined,
    prevPage: undefined,
    totalCount: 0,
    results: []
  })

  async function listAppointmentRequests(request: AppointmentRequestApiListAppointmentRequestsRequest) {
    await callWithResponse(() =>
      getAppointmentRequestApi().listAppointmentRequests(
        Object.assign(request, { page: appointmentRequests.currentPage })
      )
    )
  }

  watch(callResult, (newValue) => {
    if (newValue.isSuccess()) {
      const data: ListAppointmentRequestResponse = callResult.value.getData()
      appointmentRequests.results = data.results
      appointmentRequests.nextPage = data.next
      appointmentRequests.prevPage = data.previous
      appointmentRequests.totalCount = data.count
    }
  })

  return {
    listAppointmentRequests,
    listAppointmentRequestsResult: callResult,
    appointmentRequests
  }
}

export const useGetAppointmentRequest = () => {
  const { getAppointmentRequestApi, callWithResponse, callResult } = useAsclepius()
  const appointmentRequest = ref<AppointmentRequest>()

  async function getAppointmentRequest(id: string) {
    appointmentRequest.value = await callWithResponse<AppointmentRequest>(() =>
      getAppointmentRequestApi().getAppointmentRequest({
        appointmentRequestId: id
      })
    )
  }

  return {
    getAppointmentRequest,
    getAppointmentRequestResult: callResult,
    appointmentRequest
  }
}

export const useCancelAppointmentRequest = () => {
  const { getAppointmentRequestApi, callWithResponse, callResult } = useAsclepius()

  async function cancelAppointmentRequest(id: string, request: CancelAppointmentRequestRequest) {
    await callWithResponse(() =>
      getAppointmentRequestApi().cancelAppointmentRequest({
        appointmentRequestId: id,
        cancelAppointmentRequestRequest: request
      })
    )
  }

  return {
    cancelAppointmentRequest,
    cancelAppointmentRequestResult: callResult
  }
}

export const useFulfillAppointmentRequest = () => {
  const { getAppointmentRequestApi, callWithResponse, callResult } = useAsclepius()

  async function fulfillAppointmentRequest(id: string, request: FulfillAppointmentRequestRequest) {
    await callWithResponse(() =>
      getAppointmentRequestApi().fulfillAppointmentRequest({
        appointmentRequestId: id,
        fulfillAppointmentRequestRequest: request
      })
    )
  }

  return {
    fulfillAppointmentRequest,
    fulfillAppointmentRequestResult: callResult
  }
}

export const useListRecommendedAvailabilities = () => {
  const { getAppointmentRequestApi, callWithResponse, callResult } = useAsclepius()
  const recommendedAvailabilities = ref<Array<Availability>>([])

  async function listRecommendedAvailabilities(appointmentRequestId: string) {
    const response = await callWithResponse<ListRecommendedAvailabilitiesResponse>(() =>
      getAppointmentRequestApi().listRecommendedAvailabilities({
        appointmentRequestId: appointmentRequestId
      })
    )
    // # TODO: remove this logic when the optimal availabilities API is fixed
    recommendedAvailabilities.value = response.availabilities
  }

  return {
    listRecommendedAvailabilities,
    listRecommendedAvailabilitiesResult: callResult,
    recommendedAvailabilities
  }
}
